/**
 * Rolling temporal averager for low-light denoising.
 *
 * A single frame from a phone camera at low light is sensor-noise heavy
 * because the driver raises ISO/gain to compensate for the short exposure
 * time it needs to keep up a live preview frame rate. Averaging a few
 * consecutive frames cancels random sensor noise while preserving the
 * (static) barcode pattern — the same trick night-mode camera pipelines
 * use — but only pays off when the scene is actually holding still; if the
 * user is still moving the phone into position, averaging would just smear
 * motion blur across frames and make things worse. `push()` therefore
 * tracks mean absolute frame-to-frame difference and reports whether the
 * scene is stable enough for the average to be trustworthy.
 */
export class TemporalDenoiser {
  private buffer: Uint8ClampedArray[] = [];
  private readonly maxFrames: number;
  private lastMotion = 0;

  constructor(maxFrames = 3) {
    this.maxFrames = maxFrames;
  }

  reset(): void {
    this.buffer = [];
    this.lastMotion = 0;
  }

  /** Feed a new luma frame; returns the temporally averaged frame plus a stability flag. */
  push(luma: Uint8ClampedArray): { averaged: Uint8ClampedArray; stable: boolean } {
    if (this.buffer.length > 0) {
      this.lastMotion = meanAbsDiff(this.buffer[this.buffer.length - 1], luma);
    }
    this.buffer.push(luma);
    if (this.buffer.length > this.maxFrames) this.buffer.shift();

    const stable = this.lastMotion < 6; // ~2% mean luma delta between frames
    if (!stable || this.buffer.length < 2) {
      return { averaged: luma, stable };
    }

    const out = new Uint8ClampedArray(luma.length);
    for (const frame of this.buffer) {
      for (let i = 0; i < out.length; i++) out[i] += frame[i] / this.buffer.length;
    }
    return { averaged: out, stable };
  }
}

function meanAbsDiff(a: Uint8ClampedArray, b: Uint8ClampedArray): number {
  const stride = 4;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < a.length; i += stride) {
    sum += Math.abs(a[i] - b[i]);
    count++;
  }
  return sum / count;
}
